import { useState, useMemo, useEffect, useRef } from 'react';
import {
  useMyItems,
  useCreatePersonalItem,
  useAddItemToGroup,
  useRemoveItemFromGroup,
  MyItem,
} from '@/hooks/useMyItems';
import {
  useDeleteItem,
  useUpdateItem,
  PowerSource,
  CirculationMode,
  ItemWithOwner,
} from '@/hooks/useItems';
import { POWER_SOURCES, CIRCULATION_MODES, formatPowerSourceLabel } from '@/lib/constants';
import {
  useInitiateReturn,
  useConfirmReturn,
  useCancelReturn,
  useAcceptLoanProposal,
  useCancelLoanProposal,
} from '@/hooks/useLoans';
import { useGroups } from '@/hooks/useGroups';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { ImageUpload } from '@/components/ImageUpload';
import { Package, Search, X, ArrowLeft, Lock } from 'lucide-react';
import { compareDueUrgency } from '@/components/design/DueDateBadge';
// BarcodeScanner removed from UI — code preserved in src/components/BarcodeScanner.tsx for future reintroduction
import { CategoryDetailsFields } from '@/components/CategoryDetailsFields';
import {
  findItemCategoryByText,
  isToolCategory,
  ITEM_CATEGORY_OPTIONS,
  type ItemCategory,
} from '@/lib/itemCategoryConfig';
import { getCategoryFieldCopy } from '@/lib/itemCategoryCopy';
import {
  createEmptyCategoryDetailsState,
  deleteCategoryDetails,
  loadCategoryDetails,
  saveCategoryDetails,
  validateCategoryDetails,
  type CategoryDetailsState,
} from '@/lib/itemCategoryDetails';
import { T, RADIUS, SHADOW } from '@/lib/tokens';
import { LoanRequestsList } from '@/components/LoanRequestsList';
import { BorrowedItemsList } from '@/components/BorrowedItemsList';
import { FilterTabStrip } from '@/components/design/FilterTabStrip';
import { ItemGrid } from '@/components/design/ItemGrid';
import { ItemDetailPanel } from '@/components/design/ItemDetailPanel';
import { Layout } from '@/components/design/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { LendFromMyItemsDialog } from '@/components/LendFromMyItemsDialog';
import { ConfirmReturnDialog } from '@/components/ConfirmReturnDialog';
import { useSubscription } from '@/hooks/useSubscription';
import { useFrozenItemCount, useGraceFreezeCandidates } from '@/hooks/useFrozenItems';
import { useNavigate } from 'react-router-dom';
import { PhotoSourceTabs } from '@/components/PhotoSourceTabs';
import { PhotoLibraryPicker } from '@/components/PhotoLibraryPicker';
import { usePhotoLibrary } from '@/hooks/usePhotoLibrary';
import { findBestLibraryMatch } from '@/lib/photoLibraryMatch';
import { isLibraryUrl } from '@/lib/photoLibraryTypes';

const MY_ITEMS_TABS = ['All', 'In a Group', 'Ungrouped'];

/**
 *
 */
export default function MyItems() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isInGracePeriod, gracePeriodEndsAt } = useSubscription();
  const { data: frozenItemCount = 0 } = useFrozenItemCount();
  const { data: graceFreezeCandidates } = useGraceFreezeCandidates(isInGracePeriod);
  const graceFreezeIds = new Set(
    (graceFreezeCandidates ?? []).map((c) => c.id),
  );
  const {
    data: itemsData,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMyItems();
  const items = itemsData?.pages.flatMap((p) => p.items);
  const { data: groups } = useGroups();
  const createItem = useCreatePersonalItem();
  const deleteItem = useDeleteItem();
  const updateItem = useUpdateItem();
  const addToGroup = useAddItemToGroup();
  const removeFromGroup = useRemoveItemFromGroup();
  const initiateReturn = useInitiateReturn();
  const confirmReturn = useConfirmReturn();
  const cancelReturn = useCancelReturn();
  const acceptLoanProposal = useAcceptLoanProposal();
  const cancelLoanProposal = useCancelLoanProposal();

  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('user_id', user!.id)
        .single();
      return data;
    },
    enabled: !!user,
  });
  const displayName = profile?.display_name || user?.email || 'You';

  const [addItemOpen, setAddItemOpen] = useState(false);
  const [editItemOpen, setEditItemOpen] = useState(false);
  const [lendDialogOpen, setLendDialogOpen] = useState(false);
  const [assignGroupOpen, setAssignGroupOpen] = useState(false);
  const [leaveGroupOpen, setLeaveGroupOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<MyItem | null>(null);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('All');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<MyItem | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnDialogLoanId, setReturnDialogLoanId] = useState<string | null>(null);
  const [returnDialogItemName, setReturnDialogItemName] = useState('');
  const [returnDialogMode, setReturnDialogMode] = useState<'initiate' | 'confirm'>('initiate');

  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCategory, setEditCategory] = useState<ItemCategory | ''>('');
  const [editBrand, setEditBrand] = useState('');
  const [editPowerSource, setEditPowerSource] = useState<PowerSource | ''>('');
  const [editPhotoUrl, setEditPhotoUrl] = useState<string | null>(null);
  const [editCirculationMode, setEditCirculationMode] = useState<CirculationMode>(
    'RETURN_TO_OWNER_REQUIRED',
  );
  const [editCategoryDetails, setEditCategoryDetails] = useState<CategoryDetailsState>(
    createEmptyCategoryDetailsState(),
  );
  const [editOriginalCategory, setEditOriginalCategory] = useState<ItemCategory | ''>('');

  const [itemName, setItemName] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [itemCategory, setItemCategory] = useState<ItemCategory | ''>('');
  const [itemBrand, setItemBrand] = useState('');
  const [itemPowerSource, setItemPowerSource] = useState<PowerSource | ''>('');
  const [itemPhotoUrl, setItemPhotoUrl] = useState<string | null>(null);
  const [itemCirculationMode, setItemCirculationMode] = useState<CirculationMode>(
    'RETURN_TO_OWNER_REQUIRED',
  );
  const [itemCategoryDetails, setItemCategoryDetails] = useState<CategoryDetailsState>(
    createEmptyCategoryDetailsState(),
  );
  const [addPhotoTab, setAddPhotoTab] = useState<'upload' | 'library'>('upload');
  const [editPhotoTab, setEditPhotoTab] = useState<'upload' | 'library'>('upload');
  const [addDetailsOpen, setAddDetailsOpen] = useState(false);
  const userHasSetPhoto = useRef(false);

  const { allItems: addLibraryItems } = usePhotoLibrary(itemCategory || undefined);
  const { allItems: editLibraryItems } = usePhotoLibrary(editCategory || undefined);

  /**
   * Convert MyItem to ItemWithOwner for shared components.
   * @param item
   */
  const toItemWithOwner = (item: MyItem): ItemWithOwner => ({
    ...item,
    owner_name: displayName,
  });

  // Auto-suggest a library photo when adding a tool
  useEffect(() => {
    if (userHasSetPhoto.current) return;
    if (!itemCategory || !isToolCategory(itemCategory)) return;
    if (itemName.length < 3) return;

    const timer = setTimeout(() => {
      const match = findBestLibraryMatch(itemName, addLibraryItems);
      if (match?.url) {
        setItemPhotoUrl(match.url);
        setAddPhotoTab('library');
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [itemName, itemCategory, addLibraryItems]);

  const handleAddItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!itemName.trim()) return;
    try {
      const detailValidationError = validateCategoryDetails(itemCategory, itemCategoryDetails);
      if (detailValidationError) throw new Error(detailValidationError);

      const createdItem = await createItem.mutateAsync({
        name: itemName.trim(),
        description: itemDescription.trim() || undefined,
        category: itemCategory || undefined,
        brand: isToolCategory(itemCategory) ? itemBrand.trim() || undefined : undefined,
        powerSource: itemPowerSource || undefined,
        photoUrl: itemPhotoUrl || undefined,
        circulationMode: itemCirculationMode,
      });

      await saveCategoryDetails(createdItem.id, itemCategory, itemCategoryDetails);

      toast.success('Item added to your inventory!');
      setAddItemOpen(false);
      setItemName('');
      setItemDescription('');
      setItemCategory('');
      setItemBrand('');
      setItemPowerSource('');
      setItemPhotoUrl(null);
      setItemCirculationMode('RETURN_TO_OWNER_REQUIRED');
      setItemCategoryDetails(createEmptyCategoryDetailsState());
      setAddPhotoTab('upload');
      setAddDetailsOpen(false);
      userHasSetPhoto.current = false;
    } catch {
      // Error toast handled by useCreateItem onError
    }
  };

  const openDeleteConfirm = (item: ItemWithOwner) => {
    const myItem = items?.find((i) => i.id === item.id);
    if (myItem) {
      setItemToDelete(myItem);
      setDeleteConfirmOpen(true);
    }
  };

  const handleDeleteItem = async () => {
    if (!itemToDelete) return;
    try {
      await deleteItem.mutateAsync({ id: itemToDelete.id });
      toast.success('Item deleted');
    } catch {
      // Error toast handled by useDeleteItem onError
    }
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  const handleInitiateReturn = async (item: ItemWithOwner) => {
    if (!item.active_loan) return;
    // Borrower gets the condition dialog; owner calls mutation directly
    if (user && item.active_loan.borrower_id === user.id) {
      setReturnDialogLoanId(item.active_loan.id);
      setReturnDialogItemName(item.name);
      setReturnDialogMode('initiate');
      setReturnDialogOpen(true);
      return;
    }
    try {
      await initiateReturn.mutateAsync({ loanId: item.active_loan.id });
      toast.success('Return initiated — waiting for confirmation');
    } catch {
      // Error toast handled by useInitiateReturn onError
    }
  };

  const handleConfirmReturn = async (item: ItemWithOwner) => {
    if (!item.active_loan) return;
    // Borrower gets the condition dialog; owner calls mutation directly
    if (user && item.active_loan.borrower_id === user.id) {
      setReturnDialogLoanId(item.active_loan.id);
      setReturnDialogItemName(item.name);
      setReturnDialogMode('confirm');
      setReturnDialogOpen(true);
      return;
    }
    try {
      await confirmReturn.mutateAsync({ loanId: item.active_loan.id });
      toast.success('Return confirmed!');
      setSelectedItemId(null);
    } catch {
      // Error toast handled by useConfirmReturn onError
    }
  };

  const handleCancelReturn = async (item: ItemWithOwner) => {
    if (!item.active_loan) return;
    try {
      await cancelReturn.mutateAsync({ loanId: item.active_loan.id });
      toast.success('Return cancelled');
    } catch {
      // Error toast handled by useCancelReturn onError
    }
  };

  const handleAcceptLoanProposal = async (item: ItemWithOwner) => {
    if (!item.active_loan) return;
    try {
      await acceptLoanProposal.mutateAsync({ loanId: item.active_loan.id });
      toast.success('Loan accepted!');
    } catch {
      // Error toast handled by useAcceptLoanProposal onError
    }
  };

  const handleCancelLoanProposal = async (item: ItemWithOwner) => {
    if (!item.active_loan) return;
    try {
      await cancelLoanProposal.mutateAsync({ loanId: item.active_loan.id });
      toast.success('Loan proposal cancelled');
    } catch {
      // Error toast handled by useCancelLoanProposal onError
    }
  };

  const openLendDialog = (_item: ItemWithOwner) => {
    setLendDialogOpen(true);
  };

  const openAssignDialog = (item: MyItem) => {
    setSelectedItem(item);
    setSelectedGroup('');
    setAssignGroupOpen(true);
  };

  const openEditDialog = (item: ItemWithOwner) => {
    const myItem = items?.find((i) => i.id === item.id);
    if (!myItem) return;
    openEditDialogForMyItem(myItem);
  };

  const openEditDialogForMyItem = async (item: MyItem) => {
    const normalizedCategory = item.category ? (findItemCategoryByText(item.category) ?? '') : '';
    setSelectedItem(item);
    setEditName(item.name);
    setEditDescription(item.description || '');
    setEditCategory(normalizedCategory);
    setEditBrand(isToolCategory(normalizedCategory) ? item.brand || '' : '');
    setEditPowerSource(item.power_source || '');
    setEditPhotoUrl(item.photo_url || null);
    setEditPhotoTab(item.photo_url && isLibraryUrl(item.photo_url) ? 'library' : 'upload');
    setEditCirculationMode(item.circulation_mode);
    setEditOriginalCategory(normalizedCategory);
    setEditCategoryDetails(createEmptyCategoryDetailsState());

    if (normalizedCategory) {
      try {
        const loadedDetails = await loadCategoryDetails(item.id, normalizedCategory);
        setEditCategoryDetails(loadedDetails);
      } catch (error) {
        toast.error('Could not load category details', { description: (error as Error).message });
      }
    }
    setEditItemOpen(true);
  };

  const handleEditItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem || !editName.trim()) return;
    try {
      const detailValidationError = validateCategoryDetails(editCategory, editCategoryDetails);
      if (detailValidationError) throw new Error(detailValidationError);

      await updateItem.mutateAsync({
        id: selectedItem.id,
        name: editName.trim(),
        description: editDescription.trim() || undefined,
        category: editCategory || undefined,
        brand: isToolCategory(editCategory) ? editBrand.trim() || undefined : null,
        powerSource: isToolCategory(editCategory) ? editPowerSource || undefined : null,
        photoUrl: editPhotoUrl || undefined,
        circulationMode: editCirculationMode,
      });

      if (editOriginalCategory && editOriginalCategory !== editCategory) {
        await deleteCategoryDetails(selectedItem.id, editOriginalCategory);
      }
      await saveCategoryDetails(selectedItem.id, editCategory, editCategoryDetails);

      toast.success('Item updated!');
      setEditItemOpen(false);
      setSelectedItem(null);
    } catch {
      // Error toast handled by useUpdateItem onError
    }
  };

  const handleAssignToGroup = async () => {
    if (!selectedItem || !selectedGroup) return;
    try {
      await addToGroup.mutateAsync({ itemId: selectedItem.id, groupId: selectedGroup });
      toast.success('Item added to group!');
      setAssignGroupOpen(false);
      setSelectedItem(null);
      setSelectedGroup('');
    } catch (error) {
      toast.error('Failed to add to group', { description: (error as Error).message });
    }
  };

  const handleRemoveFromGroup = async (item: MyItem, groupId: string) => {
    try {
      await removeFromGroup.mutateAsync({ itemId: item.id, groupId });
      toast.success('Item removed from group');
    } catch (error) {
      toast.error('Failed to remove from group', { description: (error as Error).message });
    }
  };

  const { activeItems: filteredItems, frozenItems: filteredFrozenItems } = useMemo(() => {
    const matchesSearch = (item: MyItem) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      const matchesName = item.name.toLowerCase().includes(query);
      const matchesDescription = item.description?.toLowerCase().includes(query);
      const normalizedCategory = item.category
        ? findItemCategoryByText(item.category)
        : undefined;
      const matchesBrand =
        isToolCategory(normalizedCategory) && item.brand?.toLowerCase().includes(query);
      return matchesName || matchesDescription || matchesBrand;
    };

    const matchesTab = (item: MyItem) => {
      if (activeTab === 'In a Group' && item.groups.length === 0) return false;
      if (activeTab === 'Ungrouped' && item.groups.length > 0) return false;
      return true;
    };

    const active = items
      ?.filter((item) => !item.is_frozen && matchesSearch(item) && matchesTab(item))
      .sort((a, b) =>
        compareDueUrgency(a.active_loan?.due_at ?? null, b.active_loan?.due_at ?? null),
      ) ?? [];

    const frozen = items
      ?.filter((item) => item.is_frozen && matchesSearch(item))
      .sort((a, b) => a.name.localeCompare(b.name)) ?? [];

    return { activeItems: active, frozenItems: frozen };
  }, [items, searchQuery, activeTab]);

  const createFieldCopy = getCategoryFieldCopy(itemCategory);
  const editFieldCopy = getCategoryFieldCopy(editCategory);

  const selectedItemData = selectedItemId
    ? (items?.find((i) => i.id === selectedItemId) ?? null)
    : null;

  if (selectedItemData) {
    const itemWithOwner = toItemWithOwner(selectedItemData);
    const backArrow = (
      <button
        onClick={() => setSelectedItemId(null)}
        aria-label="Back"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: T.text,
          padding: 0,
          width: 44,
          height: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ArrowLeft size={22} />
      </button>
    );

    // Frozen item: show minimal detail panel instead of full ItemDetailPanel
    if (selectedItemData.is_frozen) {
      return (
        <Layout
          title={itemWithOwner.name}
          titleStyle={{
            fontSize: 22,
            fontWeight: 600,
            letterSpacing: '-0.02em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          leftAction={backArrow}
        >
          <div style={{ padding: '32px 20px', textAlign: 'center' }}>
            {itemWithOwner.photo_url && (
              <img
                src={itemWithOwner.photo_url}
                alt={itemWithOwner.name}
                style={{
                  width: 120,
                  height: 120,
                  objectFit: 'cover',
                  borderRadius: RADIUS.md,
                  opacity: 0.5,
                  margin: '0 auto 20px',
                  display: 'block',
                }}
              />
            )}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 12px',
                borderRadius: RADIUS.xl,
                background: T.surfaceSubtle,
                border: `1px solid ${T.border}`,
                marginBottom: 16,
              }}
            >
              <Lock size={12} color={T.textMuted} />
              <span style={{ fontSize: 12, fontWeight: 700, color: T.textMuted }}>Frozen</span>
            </div>
            <p style={{ fontSize: 15, color: T.text, fontWeight: 600, margin: '0 0 8px' }}>
              {itemWithOwner.name}
            </p>
            <p style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.65, margin: '0 0 24px' }}>
              This item is frozen and hidden from your groups.
              Upgrade your plan to reactivate all your frozen items.
            </p>
            <button
              onClick={() => navigate('/account')}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: RADIUS.md,
                background: T.accent,
                color: T.white,
                border: 'none',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                boxShadow: SHADOW.btn.accent,
              }}
            >
              Upgrade to unlock
            </button>
          </div>
        </Layout>
      );
    }

    return (
      <>
        <Layout
          title={itemWithOwner.name}
          titleStyle={{
            fontSize: 22,
            fontWeight: 600,
            letterSpacing: '-0.02em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          leftAction={backArrow}
        >
          <ItemDetailPanel
            item={itemWithOwner}
            currentUserId={user!.id}
            onBack={() => setSelectedItemId(null)}
            onInitiateReturn={handleInitiateReturn}
            onConfirmReturn={handleConfirmReturn}
            onCancelReturn={handleCancelReturn}
            onLend={openLendDialog}
            onEdit={openEditDialog}
            onDelete={openDeleteConfirm}
            onAcceptProposal={handleAcceptLoanProposal}
            onCancelProposal={handleCancelLoanProposal}
            itemGroups={selectedItemData.groups}
            onAddToGroup={() => openAssignDialog(selectedItemData)}
          />
        </Layout>

        {/* Return Condition Dialog */}
        <ConfirmReturnDialog
          loanId={returnDialogLoanId}
          itemName={returnDialogItemName}
          mode={returnDialogMode}
          open={returnDialogOpen}
          onOpenChange={setReturnDialogOpen}
        />

        {/* Lend to Member Dialog */}
        <LendFromMyItemsDialog
          open={lendDialogOpen}
          onOpenChange={setLendDialogOpen}
          item={itemWithOwner}
          currentUserId={user!.id}
          itemGroups={selectedItemData.groups}
        />

        {/* Edit Item Dialog */}
        <Dialog open={editItemOpen} onOpenChange={setEditItemOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Item</DialogTitle>
              <DialogDescription>Update the details for "{selectedItem?.name}".</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEditItem} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="editName">Name</Label>
                <Input
                  id="editName"
                  placeholder={editFieldCopy.namePlaceholder}
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">{editFieldCopy.nameHelpText}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="editDescription">Description (optional)</Label>
                <Input
                  id="editDescription"
                  placeholder={editFieldCopy.descriptionPlaceholder}
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editCategory">Category</Label>
                <Select
                  value={editCategory}
                  onValueChange={(category) => {
                    const nextCategory = category as ItemCategory;
                    setEditCategory(nextCategory);
                    if (!isToolCategory(nextCategory)) {
                      setEditBrand('');
                      setEditPowerSource('');
                      setEditPhotoTab('upload');
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {ITEM_CATEGORY_OPTIONS.map((category) => (
                      <SelectItem key={category.value} value={category.value}>
                        {category.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {isToolCategory(editCategory) && (
                <div className="space-y-2">
                  <Label htmlFor="editBrand">Brand (optional)</Label>
                  <Input
                    id="editBrand"
                    placeholder={editFieldCopy.brandPlaceholder}
                    value={editBrand}
                    onChange={(e) => setEditBrand(e.target.value)}
                  />
                </div>
              )}
              {isToolCategory(editCategory) && (
                <div className="space-y-2">
                  <Label htmlFor="editPowerSource">Power Source</Label>
                  <Select
                    value={editPowerSource}
                    onValueChange={(val) => setEditPowerSource(val as PowerSource)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select power source" />
                    </SelectTrigger>
                    <SelectContent>
                      {POWER_SOURCES.map((ps) => (
                        <SelectItem key={ps.value} value={ps.value}>
                          {ps.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {editCategory && !isToolCategory(editCategory) && (
                <CategoryDetailsFields
                  category={editCategory}
                  details={editCategoryDetails}
                  onChange={setEditCategoryDetails}
                  idPrefix="edit-"
                />
              )}
              <div className="space-y-2">
                <Label>Photo</Label>
                {isToolCategory(editCategory) && editLibraryItems.length > 0 ? (
                  <>
                    <PhotoSourceTabs activeTab={editPhotoTab} onTabChange={setEditPhotoTab} />
                    {editPhotoTab === 'upload' ? (
                      <ImageUpload
                        value={editPhotoUrl && !isLibraryUrl(editPhotoUrl) ? editPhotoUrl : null}
                        onChange={setEditPhotoUrl}
                      />
                    ) : (
                      <PhotoLibraryPicker
                        category={editCategory}
                        onSelect={setEditPhotoUrl}
                        selectedUrl={editPhotoUrl && isLibraryUrl(editPhotoUrl) ? editPhotoUrl : undefined}
                      />
                    )}
                  </>
                ) : (
                  <ImageUpload value={editPhotoUrl} onChange={setEditPhotoUrl} />
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="editCirculationMode">Circulation Rules</Label>
                <Select
                  value={editCirculationMode}
                  onValueChange={(val) => setEditCirculationMode(val as CirculationMode)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select circulation mode" />
                  </SelectTrigger>
                  <SelectContent>
                    {CIRCULATION_MODES.map((cm) => (
                      <SelectItem key={cm.value} value={cm.value}>
                        <div className="flex flex-col">
                          <span>{cm.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {CIRCULATION_MODES.find((cm) => cm.value === editCirculationMode)?.description}
                </p>
              </div>
              <button
                type="submit"
                disabled={updateItem.isPending}
                style={{
                  width: '100%',
                  padding: '13px',
                  borderRadius: RADIUS.md,
                  background: T.accent,
                  color: T.white,
                  border: 'none',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: updateItem.isPending ? 'default' : 'pointer',
                  fontFamily: 'inherit',
                  opacity: updateItem.isPending ? 0.7 : 1,
                  boxShadow: SHADOW.btn.accent,
                }}
              >
                {updateItem.isPending ? 'Saving…' : 'Save Changes'}
              </button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                {itemToDelete?.active_loan
                  ? 'This item is currently on loan and cannot be deleted. Please return it first.'
                  : `This will permanently delete "${itemToDelete?.name}". This action cannot be undone.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              {!itemToDelete?.active_loan && (
                <AlertDialogAction onClick={handleDeleteItem}>Delete</AlertDialogAction>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Assign to Group Dialog */}
        <Dialog open={assignGroupOpen} onOpenChange={setAssignGroupOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add to Group</DialogTitle>
              <DialogDescription>
                Choose a group to share "{selectedItem?.name}" with.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Group</Label>
                <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a group" />
                  </SelectTrigger>
                  <SelectContent>
                    {groups
                      ?.filter((group) => !selectedItem?.groups.some((g) => g.id === group.id))
                      .map((group) => (
                        <SelectItem key={group.id} value={group.id}>
                          {group.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <button
                onClick={handleAssignToGroup}
                disabled={!selectedGroup || addToGroup.isPending}
                style={{
                  width: '100%',
                  padding: '13px',
                  borderRadius: RADIUS.md,
                  background: T.accent,
                  color: T.white,
                  border: 'none',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: !selectedGroup || addToGroup.isPending ? 'default' : 'pointer',
                  fontFamily: 'inherit',
                  opacity: !selectedGroup || addToGroup.isPending ? 0.7 : 1,
                  boxShadow: SHADOW.btn.accent,
                }}
              >
                {addToGroup.isPending ? 'Adding…' : 'Add to Group'}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  const headerContent = (
    <>
      {/* Search bar */}
      <div
        style={{
          background: T.surfaceSubtle,
          border: `1px solid ${T.border}`,
          borderRadius: RADIUS.md,
          boxShadow: SHADOW.sm,
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 14,
        }}
      >
        <Search size={14} color={T.textMuted} />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search items…"
          style={{
            flex: 1,
            border: 'none',
            background: 'transparent',
            outline: 'none',
            color: searchQuery ? T.text : T.textMuted,
            fontSize: 14,
            fontFamily: 'inherit',
          }}
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            aria-label="Clear search"
            style={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              color: T.textMuted,
              padding: 0,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Filter tabs */}
      <FilterTabStrip tabs={MY_ITEMS_TABS} activeTab={activeTab} onTabChange={setActiveTab} />
    </>
  );

  return (
    <>
      <Layout title="My Items" headerContent={headerContent}>
        {/* Post-freeze banner */}
        {frozenItemCount > 0 && !isInGracePeriod && (
          <div
            style={{
              background: T.warningSoft,
              border: `1px solid ${T.accentBorder}`,
              borderRadius: RADIUS.lg,
              padding: '12px 20px',
              marginBottom: 12,
            }}
          >
            <span style={{ fontSize: 14, color: '#B85C00' }}>
              You have {frozenItemCount} frozen {frozenItemCount === 1 ? 'item' : 'items'} hidden from your groups.{' '}
              <span
                style={{ fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => navigate('/account')}
              >
                Upgrade to unlock
              </span>
            </span>
          </div>
        )}

        {/* Grace period banner */}
        {isInGracePeriod && graceFreezeIds.size > 0 && (
          <div
            style={{
              background: T.warningSoft,
              border: `1px solid ${T.warningBorder}`,
              borderRadius: RADIUS.md,
              padding: 12,
              marginBottom: 12,
            }}
          >
            <p style={{ fontSize: 13, fontWeight: 600, color: T.warning, marginBottom: 4 }}>
              Your subscription has ended
            </p>
            <p style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.65, margin: 0 }}>
              {graceFreezeIds.size} {graceFreezeIds.size === 1 ? 'item' : 'items'} will be frozen on{' '}
              {gracePeriodEndsAt
                ? new Date(gracePeriodEndsAt).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'long',
                  })
                : 'soon'}
              .{' '}
              <span
                style={{ color: T.accent, fontWeight: 600, cursor: 'pointer' }}
                onClick={() => navigate('/account')}
              >
                Upgrade
              </span>{' '}
              to keep them all active.
            </p>
          </div>
        )}

        {/* Pending loan requests */}
        <LoanRequestsList />

        {/* Items the user is currently borrowing */}
        <BorrowedItemsList />

        {/* Add item button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 0 4px' }}>
          <button
            onClick={() => setAddItemOpen(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '7px 14px',
              borderRadius: RADIUS.md,
              border: 'none',
              background: T.accent,
              color: T.white,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              boxShadow: SHADOW.btn.accent,
            }}
          >
            + Add Item
          </button>
        </div>

        {/* Item list */}
        {isLoading ? (
          <div style={{ textAlign: 'center', color: T.textMuted, padding: '32px 0', fontSize: 14 }}>
            Loading items…
          </div>
        ) : isError ? (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <p style={{ fontSize: 15, fontWeight: 600, color: T.danger, margin: '0 0 6px' }}>
              Failed to load items
            </p>
            <p style={{ fontSize: 13, color: T.textMuted, margin: 0 }}>
              Please check your connection and try again.
            </p>
          </div>
        ) : filteredItems?.length === 0 && filteredFrozenItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '64px 20px' }}>
            <div
              style={{ display: 'flex', justifyContent: 'center', marginBottom: 16, opacity: 0.25 }}
            >
              <Package size={52} color={T.textMuted} />
            </div>
            {activeTab === 'Ungrouped' && (items?.length ?? 0) > 0 ? (
              <>
                <p style={{ fontSize: 17, fontWeight: 600, color: T.text, margin: '0 0 8px' }}>
                  All your items are in groups
                </p>
                <p style={{ fontSize: 14, color: T.textSec, lineHeight: 1.65, margin: 0 }}>
                  Items you haven't added to a group will appear here.
                </p>
              </>
            ) : (
              <>
                <p style={{ fontSize: 17, fontWeight: 600, color: T.text, margin: '0 0 8px' }}>
                  You haven't added any items yet
                </p>
                <p style={{ fontSize: 14, color: T.textSec, lineHeight: 1.65, margin: '0 0 24px' }}>
                  Add items to your catalogue to start sharing with your groups.
                </p>
                <button
                  onClick={() => setAddItemOpen(true)}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '12px',
                    borderRadius: RADIUS.md,
                    background: T.accent,
                    color: T.white,
                    border: 'none',
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    boxShadow: SHADOW.btn.accent,
                  }}
                >
                  + Add Item
                </button>
              </>
            )}
          </div>
        ) : (
          <>
            {/* Grace period "will be frozen" labels are handled via the
                graceFreezeIds set — items matching those IDs get a subtle
                label in the ItemGrid's ItemListRow/ItemCard. We pass the
                grace-period item IDs through the item data itself via
                a wrapper. */}
            {filteredItems.length > 0 && (
              <ItemGrid
                items={filteredItems.map((item) => ({
                  ...toItemWithOwner(item),
                  // Attach grace-period indicator for rendering
                  _willBeFrozen: isInGracePeriod && graceFreezeIds.has(item.id),
                }))}
                currentUserId={user!.id}
                onItemClick={(id) => setSelectedItemId(id)}
              />
            )}

            {hasNextPage && (
              <div style={{ textAlign: 'center', padding: '12px 0' }}>
                <button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  style={{
                    border: `1px solid ${T.border}`,
                    background: T.surfaceSubtle,
                    borderRadius: RADIUS.sm,
                    padding: '8px 20px',
                    cursor: isFetchingNextPage ? 'default' : 'pointer',
                    color: T.textSec,
                    fontSize: 13,
                    fontFamily: 'inherit',
                  }}
                >
                  {isFetchingNextPage ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}

            {/* Frozen items section */}
            {filteredFrozenItems.length > 0 && (
              <>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: T.textMuted,
                    padding: '20px 0 8px',
                  }}
                >
                  Frozen Items
                </div>
                <div style={{ opacity: 0.5 }}>
                  {filteredFrozenItems.map((item, idx) => (
                    <div
                      key={item.id}
                      onClick={() => setSelectedItemId(item.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 0',
                        borderBottom:
                          idx < filteredFrozenItems.length - 1
                            ? `1px solid ${T.border}`
                            : 'none',
                        cursor: 'pointer',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Lock size={14} color={T.textMuted} />
                        <span style={{ fontSize: 14, color: T.text }}>{item.name}</span>
                      </div>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          padding: '2px 8px',
                          borderRadius: RADIUS.xl,
                          background: T.surfaceSubtle,
                          color: T.textMuted,
                          border: `1px solid ${T.border}`,
                        }}
                      >
                        Frozen
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </Layout>

      {/* Add Item Dialog */}
      <Dialog open={addItemOpen} onOpenChange={setAddItemOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Item</DialogTitle>
            <DialogDescription>
              Add an item to your personal inventory. You can share it with groups later.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddItem} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="itemName">Name</Label>
              <Input
                id="itemName"
                placeholder={createFieldCopy.namePlaceholder}
                value={itemName}
                onChange={(e) => setItemName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="itemCategory">Category</Label>
              <Select
                value={itemCategory}
                onValueChange={(category) => {
                  const nextCategory = category as ItemCategory;
                  setItemCategory(nextCategory);
                  if (!isToolCategory(nextCategory)) {
                    setItemBrand('');
                    setItemPowerSource('');
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {ITEM_CATEGORY_OPTIONS.map((category) => (
                    <SelectItem key={category.value} value={category.value}>
                      {category.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Photo (optional)</Label>
              {isToolCategory(itemCategory) && addLibraryItems.length > 0 ? (
                <>
                  <PhotoSourceTabs activeTab={addPhotoTab} onTabChange={setAddPhotoTab} />
                  {addPhotoTab === 'upload' ? (
                    <ImageUpload
                      value={itemPhotoUrl && !isLibraryUrl(itemPhotoUrl) ? itemPhotoUrl : null}
                      onChange={(url) => {
                        setItemPhotoUrl(url);
                        if (url) userHasSetPhoto.current = true;
                        else userHasSetPhoto.current = false;
                      }}
                    />
                  ) : (
                    <PhotoLibraryPicker
                      category={itemCategory}
                      onSelect={(url) => {
                        setItemPhotoUrl(url);
                        userHasSetPhoto.current = true;
                      }}
                      selectedUrl={itemPhotoUrl && isLibraryUrl(itemPhotoUrl) ? itemPhotoUrl : undefined}
                    />
                  )}
                </>
              ) : (
                <ImageUpload value={itemPhotoUrl} onChange={setItemPhotoUrl} />
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="itemCirculationMode">Circulation Rules</Label>
              <Select
                value={itemCirculationMode}
                onValueChange={(val) => setItemCirculationMode(val as CirculationMode)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select circulation mode" />
                </SelectTrigger>
                <SelectContent>
                  {CIRCULATION_MODES.map((cm) => (
                    <SelectItem key={cm.value} value={cm.value}>
                      <div className="flex flex-col">
                        <span>{cm.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {CIRCULATION_MODES.find((cm) => cm.value === itemCirculationMode)?.description}
              </p>
            </div>
            {/* Additional details expandable */}
            <button
              type="button"
              onClick={() => setAddDetailsOpen((prev) => !prev)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                padding: '12px 16px',
                background: T.surfaceSubtle,
                border: `1px solid ${T.border}`,
                borderRadius: RADIUS.sm,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Additional details</span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  color: T.textMuted,
                  transform: addDetailsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 150ms ease',
                  flexShrink: 0,
                }}
              >
                <path d="M 4 6 L 8 10 L 12 6" />
              </svg>
            </button>
            {addDetailsOpen && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="itemDescription">Description (optional)</Label>
                  <Input
                    id="itemDescription"
                    placeholder={createFieldCopy.descriptionPlaceholder}
                    value={itemDescription}
                    onChange={(e) => setItemDescription(e.target.value)}
                  />
                </div>
                {isToolCategory(itemCategory) && (
                  <div className="space-y-2">
                    <Label htmlFor="itemBrand">Brand (optional)</Label>
                    <Input
                      id="itemBrand"
                      placeholder={createFieldCopy.brandPlaceholder}
                      value={itemBrand}
                      onChange={(e) => setItemBrand(e.target.value)}
                    />
                  </div>
                )}
                {isToolCategory(itemCategory) && (
                  <div className="space-y-2">
                    <Label htmlFor="itemPowerSource">Power Source (optional)</Label>
                    <Select
                      value={itemPowerSource}
                      onValueChange={(val) => setItemPowerSource(val as PowerSource)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select power source" />
                      </SelectTrigger>
                      <SelectContent>
                        {POWER_SOURCES.map((ps) => (
                          <SelectItem key={ps.value} value={ps.value}>
                            {ps.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {!isToolCategory(itemCategory) && itemCategory && (
                  <CategoryDetailsFields
                    category={itemCategory}
                    details={itemCategoryDetails}
                    onChange={setItemCategoryDetails}
                    idPrefix="create-"
                  />
                )}
              </div>
            )}
            <button
              type="submit"
              disabled={createItem.isPending}
              style={{
                width: '100%',
                padding: '13px',
                borderRadius: RADIUS.md,
                background: T.accent,
                color: T.white,
                border: 'none',
                fontSize: 15,
                fontWeight: 600,
                cursor: createItem.isPending ? 'default' : 'pointer',
                fontFamily: 'inherit',
                opacity: createItem.isPending ? 0.7 : 1,
                boxShadow: SHADOW.btn.accent,
              }}
            >
              {createItem.isPending ? 'Adding…' : 'Add Item'}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign to Group Dialog */}
      <Dialog open={assignGroupOpen} onOpenChange={setAssignGroupOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add to Group</DialogTitle>
            <DialogDescription>
              Choose a group to share "{selectedItem?.name}" with.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Group</Label>
              <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a group" />
                </SelectTrigger>
                <SelectContent>
                  {groups
                    ?.filter((group) => !selectedItem?.groups.some((g) => g.id === group.id))
                    .map((group) => (
                      <SelectItem key={group.id} value={group.id}>
                        {group.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <button
              onClick={handleAssignToGroup}
              disabled={!selectedGroup || addToGroup.isPending}
              style={{
                width: '100%',
                padding: '13px',
                borderRadius: RADIUS.md,
                background: T.accent,
                color: T.white,
                border: 'none',
                fontSize: 15,
                fontWeight: 600,
                cursor: !selectedGroup || addToGroup.isPending ? 'default' : 'pointer',
                fontFamily: 'inherit',
                opacity: !selectedGroup || addToGroup.isPending ? 0.7 : 1,
                boxShadow: SHADOW.btn.accent,
              }}
            >
              {addToGroup.isPending ? 'Adding…' : 'Add to Group'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove from Group Dialog */}
      <Dialog open={leaveGroupOpen} onOpenChange={setLeaveGroupOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove from Group</DialogTitle>
            <DialogDescription>
              Choose which group to remove "{selectedItem?.name}" from.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Group</Label>
              <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a group" />
                </SelectTrigger>
                <SelectContent>
                  {selectedItem?.groups.map((g) => (
                    <SelectItem key={g.id} value={g.id}>
                      {g.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <button
              onClick={async () => {
                if (!selectedItem || !selectedGroup) return;
                await handleRemoveFromGroup(selectedItem, selectedGroup);
                setLeaveGroupOpen(false);
                setSelectedItem(null);
                setSelectedGroup('');
              }}
              disabled={!selectedGroup || removeFromGroup.isPending}
              style={{
                width: '100%',
                padding: '13px',
                borderRadius: RADIUS.md,
                background: T.danger,
                color: T.white,
                border: 'none',
                fontSize: 15,
                fontWeight: 600,
                cursor: !selectedGroup || removeFromGroup.isPending ? 'default' : 'pointer',
                fontFamily: 'inherit',
                opacity: !selectedGroup || removeFromGroup.isPending ? 0.7 : 1,
                boxShadow: SHADOW.btn.danger,
              }}
            >
              {removeFromGroup.isPending ? 'Removing…' : 'Remove from Group'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation (for list view) */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              {itemToDelete?.active_loan
                ? 'This item is currently on loan and cannot be deleted. Please return it first.'
                : `This will permanently delete "${itemToDelete?.name}". This action cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            {!itemToDelete?.active_loan && (
              <AlertDialogAction onClick={handleDeleteItem}>Delete</AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
